import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Lock, MoreHorizontal, Pencil, Plus } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { api } from '@/lib/api';
import { ALL_ROLES, ROLE_LABELS, stranaForRole, stranaForRoles } from '@/lib/roleStrana';
import { kontrolaHesla, HESLO_HINT } from '@/lib/passwordPolicy';
import type {
  CreateUserInput,
  Role,
  UpdateHesloInput,
  UpdateUserInput,
  UpdateUserRolesInput,
  UserDetail,
  UserListItem,
} from '@/types/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';

const ENDPOINT = '/uzivatele';
const QUERY_KEY_ACTIVE = ['uzivatele', { includeInactive: false }] as const;
const QUERY_KEY_ALL = ['uzivatele', { includeInactive: true }] as const;

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; item: UserListItem }
  | { mode: 'password'; item: UserListItem };

export function UzivatelePage() {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <div className="flex items-start">
        <Card className="max-w-md">
          <CardHeader>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-md bg-destructive/10 text-destructive">
              <Lock className="h-5 w-5" />
            </div>
            <CardTitle>Nemáte oprávnění</CardTitle>
            <CardDescription>
              Pro zobrazení správy uživatelů je vyžadována role <strong>ADMIN</strong>.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return <AdminUsersList />;
}

function AdminUsersList() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  // Id prihlaseneho uzivatele — backend brani self-deaktivaci a self-zmene roli
  // (409). Pro vlastni radek/ucet proto zakazeme prislusne GUI prvky. Kdyz id
  // neni dostupne (undefined), nic navic neblokujeme — viz porovnani nize.
  const currentUserId = user?.id;
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });
  const [tab, setTab] = useState<'active' | 'inactive'>('active');

  // Nacitame VSECHNY uzivatele (vcetne neaktivnich) a tabbing resime na klientu —
  // jednodussi nez delat dva paralelni dotazy a invalidovat oba na kazdou
  // mutaci. Backend filtruje pres ?includeInactive=true.
  const listQuery = useQuery<UserListItem[]>({
    queryKey: QUERY_KEY_ALL,
    queryFn: () => api<UserListItem[]>(`${ENDPOINT}?includeInactive=true`),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_ALL });
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_ACTIVE });
  };

  const createMutation = useMutation({
    mutationFn: (input: CreateUserInput) =>
      api<UserDetail>(ENDPOINT, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Uživatel vytvořen' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Vytvoření selhalo', description: err.message, variant: 'destructive' }),
  });

  // Edit potrebuje DVA volani — zakladni dotaz (email/jmeno/strana) a role
  // se posilaji na samostatnem endpointu. Sekvencne, aby pripadna chyba
  // role-update byla viditelna v toastu.
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      base,
      roles,
      skipRoles,
    }: {
      id: string;
      base: UpdateUserInput;
      roles: Role[];
      // U vlastniho uctu backend brani zmene roli (409 self_zmena_roli_zakazana),
      // proto roles-endpoint vubec nevolame — email/jmeno se ulozit smi.
      skipRoles?: boolean;
    }) => {
      const baseRes = await api<UserDetail>(`${ENDPOINT}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(base),
      });
      if (skipRoles) {
        return baseRes;
      }
      const rolesBody: UpdateUserRolesInput = { roles };
      return api<UserDetail>(`${ENDPOINT}/${id}/roles`, {
        method: 'PUT',
        body: JSON.stringify(rolesBody),
      });
    },
    onSuccess: () => {
      invalidateAll();
      toast({ title: 'Uživatel upraven' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Úprava selhala', description: err.message, variant: 'destructive' }),
  });

  // Optimisticky toggle "aktivni" — nechceme cekat na server, hned prepiseme
  // cache a v pripade chyby vratime puvodni stav + toast.
  const toggleAktivniMutation = useMutation({
    mutationFn: ({ id, aktivni }: { id: string; aktivni: boolean }) =>
      api<void>(`${ENDPOINT}/${id}/aktivni`, {
        method: 'PUT',
        body: JSON.stringify({ aktivni }),
      }),
    onMutate: async ({ id, aktivni }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY_ALL });
      const previous = queryClient.getQueryData<UserListItem[]>(QUERY_KEY_ALL);
      if (previous) {
        queryClient.setQueryData<UserListItem[]>(
          QUERY_KEY_ALL,
          previous.map((u) => (u.id === id ? { ...u, aktivni } : u)),
        );
      }
      return { previous };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(QUERY_KEY_ALL, ctx.previous);
      }
      toast({ title: 'Změna stavu selhala', description: err.message, variant: 'destructive' });
    },
    onSuccess: () => {
      // Aktivni-only seznam je stale (kdyz ho nekdo cte), invalidujeme.
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_ACTIVE });
    },
  });

  // Heslo existujiciho uzivatele resime jednim dialogem (mode 'password') se dvema
  // volbami: (a) poslat reset-link e-mailem, (b) zadat heslo a ulozit primo.
  const resetHesloMutation = useMutation({
    mutationFn: (id: string) =>
      api<void>(`${ENDPOINT}/${id}/poslat-reset-heslo`, { method: 'POST' }),
    onError: (err: Error) =>
      toast({ title: 'Odeslání selhalo', description: err.message, variant: 'destructive' }),
  });

  const setHesloMutation = useMutation({
    mutationFn: ({ id, heslo }: { id: string; heslo: string }) => {
      const body: UpdateHesloInput = { noveHeslo: heslo };
      return api<void>(`${ENDPOINT}/${id}/heslo`, { method: 'PUT', body: JSON.stringify(body) });
    },
    onError: (err: Error) =>
      toast({ title: 'Nastavení hesla selhalo', description: err.message, variant: 'destructive' }),
  });

  const handlePasswordSubmit = (item: UserListItem, sendResetLink: boolean, heslo: string) => {
    if (sendResetLink) {
      resetHesloMutation.mutate(item.id, {
        onSuccess: () => {
          toast({ title: 'Reset link odeslán', description: `Odesláno na ${item.email}` });
          setDialog({ mode: 'closed' });
        },
      });
    } else {
      setHesloMutation.mutate(
        { id: item.id, heslo },
        {
          onSuccess: () => {
            toast({ title: 'Heslo nastaveno', description: item.email });
            setDialog({ mode: 'closed' });
          },
        },
      );
    }
  };

  const allUsers = listQuery.data ?? [];
  const activeUsers = allUsers.filter((u) => u.aktivni);
  const inactiveUsers = allUsers.filter((u) => !u.aktivni);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Uživatelé</h2>
          <p className="text-sm text-muted-foreground">Správa uživatelských účtů a rolí</p>
        </div>
        <Button onClick={() => setDialog({ mode: 'create' })}>
          <Plus className="mr-2 h-4 w-4" />
          Nový uživatel
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : listQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Načtení selhalo: {(listQuery.error as Error).message}
            </div>
          ) : (
            <Tabs value={tab} onValueChange={(v) => setTab(v as 'active' | 'inactive')}>
              <TabsList>
                <TabsTrigger value="active">Aktivní uživatelé ({activeUsers.length})</TabsTrigger>
                <TabsTrigger value="inactive">Neaktivní ({inactiveUsers.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="active">
                <UsersTable
                  users={activeUsers}
                  currentUserId={currentUserId}
                  onEdit={(u) => setDialog({ mode: 'edit', item: u })}
                  onToggleAktivni={(u) =>
                    toggleAktivniMutation.mutate({ id: u.id, aktivni: !u.aktivni })
                  }
                  onSetHeslo={(u) => setDialog({ mode: 'password', item: u })}
                />
              </TabsContent>
              <TabsContent value="inactive">
                <UsersTable
                  users={inactiveUsers}
                  currentUserId={currentUserId}
                  onEdit={(u) => setDialog({ mode: 'edit', item: u })}
                  onToggleAktivni={(u) =>
                    toggleAktivniMutation.mutate({ id: u.id, aktivni: !u.aktivni })
                  }
                  onSetHeslo={(u) => setDialog({ mode: 'password', item: u })}
                />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      <UserFormDialog
        state={dialog}
        currentUserId={currentUserId}
        onClose={() => setDialog({ mode: 'closed' })}
        onCreate={(input) => createMutation.mutate(input)}
        onUpdate={(id, base, roles, skipRoles) =>
          updateMutation.mutate({ id, base, roles, skipRoles })
        }
        submitting={createMutation.isPending || updateMutation.isPending}
      />

      <SetPasswordDialog
        item={dialog.mode === 'password' ? dialog.item : null}
        onClose={() => setDialog({ mode: 'closed' })}
        onSubmit={handlePasswordSubmit}
        submitting={resetHesloMutation.isPending || setHesloMutation.isPending}
      />
    </div>
  );
}

interface UsersTableProps {
  users: UserListItem[];
  /** Id prihlaseneho uzivatele (undefined = neznamy, nic neblokujeme). */
  currentUserId?: string;
  onEdit: (u: UserListItem) => void;
  onToggleAktivni: (u: UserListItem) => void;
  onSetHeslo: (u: UserListItem) => void;
}

function UsersTable({ users, currentUserId, onEdit, onToggleAktivni, onSetHeslo }: UsersTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>E-mail</TableHead>
          <TableHead>Jméno</TableHead>
          <TableHead>Role</TableHead>
          <TableHead className="w-28">Aktivní</TableHead>
          <TableHead className="w-32 text-right">Akce</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
              Žádní uživatelé.
            </TableCell>
          </TableRow>
        ) : (
          users.map((u) => {
            // Vlastni ucet — backend brani self-deaktivaci/self-zmene roli (409).
            const isSelf = currentUserId != null && u.id === currentUserId;
            return (
            <TableRow key={u.id}>
              <TableCell className="font-mono text-xs">{u.email}</TableCell>
              <TableCell>{u.jmeno}</TableCell>
              <TableCell>
                <RoleCell roles={u.roles} />
              </TableCell>
              <TableCell>
                <AktivniToggle
                  checked={u.aktivni}
                  disabled={isSelf}
                  title={isSelf ? 'Nelze upravit vlastni ucet (aktivni)' : undefined}
                  onChange={() => onToggleAktivni(u)}
                />
              </TableCell>
              <TableCell className="text-right">
                <RowActions user={u} onEdit={onEdit} onSetHeslo={onSetHeslo} />
              </TableCell>
            </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

// Strana je z UI odebrana — derivuje se z vybranych roli pri submitu
// (viz roleStrana.ts). Uzivatel muze mit vic roli (typicky ADMIN + SCHVALOVATEL),
// kazda dostane vlastni badge.
function RoleCell({ roles }: { roles: Role[] }) {
  if (roles.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <Badge
          key={r}
          variant={stranaForRole(r) === 'EXTERNI_FIRMA' ? 'secondary' : 'default'}
          className="font-normal"
        >
          {ROLE_LABELS[r]}
        </Badge>
      ))}
    </div>
  );
}

interface AktivniToggleProps {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  title?: string;
}

// Maly toggle switch — nepouzivam radix Switch (neni nainstalovan), staci
// nativni checkbox v shellu.
function AktivniToggle({ checked, onChange, disabled, title }: AktivniToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      title={title}
      onClick={onChange}
      className={
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors ' +
        (disabled ? 'cursor-not-allowed opacity-50 ' : '') +
        (checked ? 'bg-emerald-500' : 'bg-muted-foreground/30')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0.5')
        }
      />
    </button>
  );
}

interface RowActionsProps {
  user: UserListItem;
  onEdit: (u: UserListItem) => void;
  onSetHeslo: (u: UserListItem) => void;
}

function RowActions({ user, onEdit, onSetHeslo }: RowActionsProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onEdit(user);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent"
            >
              <Pencil className="h-4 w-4" />
              Upravit
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSetHeslo(user);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent"
            >
              <KeyRound className="h-4 w-4" />
              Nastavit heslo
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface FormDialogProps {
  state: DialogState;
  /** Id prihlaseneho uzivatele (undefined = neznamy, nic neblokujeme). */
  currentUserId?: string;
  onClose: () => void;
  onCreate: (input: CreateUserInput) => void;
  onUpdate: (id: string, base: UpdateUserInput, roles: Role[], skipRoles: boolean) => void;
  submitting: boolean;
}

function UserFormDialog({
  state,
  currentUserId,
  onClose,
  onCreate,
  onUpdate,
  submitting,
}: FormDialogProps) {
  const open = state.mode === 'create' || state.mode === 'edit';
  const editItem = state.mode === 'edit' ? state.item : null;
  // Editujeme-li vlastni ucet, backend brani zmene roli (409). Zamkneme proto
  // select Role a roles-update preskocime; email/jmeno menit lze.
  const isSelfEdit = editItem != null && currentUserId != null && editItem.id === currentUserId;

  const [email, setEmail] = useState('');
  const [jmeno, setJmeno] = useState('');
  // Vic roli najednou (typicky ADMIN + SCHVALOVATEL); strana se dopocte
  // z vyberu (viz roleStrana.ts) — mix stran formular nedovoli ulozit.
  const [roles, setRoles] = useState<Role[]>(['TECHNIK_INT']);
  const [heslo, setHeslo] = useState('');
  const [sendResetLink, setSendResetLink] = useState(true);

  // Odvozena strana: null = nic nevybrano NEBO mix interni x externi role.
  const strana = stranaForRoles(roles);
  const rolesMix = roles.length > 1 && strana === null;
  const rolesVadne = roles.length === 0 || strana === null;

  // Policy validace plati jen u create vetve, kdyz NEni zaskrtnut reset link
  // a heslo je zadane. E-mail je k dispozici (zadava se ve formulari).
  const hesloAktivni = !editItem && !sendResetLink;
  const hesloDuvody = hesloAktivni && heslo.length > 0 ? kontrolaHesla(heslo, email) : [];
  const hesloVadne = hesloAktivni && (heslo.length === 0 || hesloDuvody.length > 0);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setEmail(editItem.email);
      setJmeno(editItem.jmeno);
      setRoles(editItem.roles.length > 0 ? editItem.roles : ['TECHNIK_INT']);
      setHeslo('');
      setSendResetLink(true);
    } else {
      setEmail('');
      setJmeno('');
      setRoles(['TECHNIK_INT']);
      setHeslo('');
      setSendResetLink(true);
    }
  }, [open, editItem]);

  const toggleRole = (r: Role) =>
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Strana se dopoctava z vybranych roli; mix stran sem nedojde
    // (submit je pri rolesVadne disabled). U self-editu se role neposilaji
    // (skipRoles) — kdyby derivace selhala, drzime stavajici stranu uctu.
    const stranaFinal = strana ?? (isSelfEdit ? editItem!.strana : null);
    if (stranaFinal === null) return;
    if (editItem) {
      onUpdate(
        editItem.id,
        { email: email.trim(), jmeno: jmeno.trim(), strana: stranaFinal },
        roles,
        isSelfEdit,
      );
    } else {
      const input: CreateUserInput = {
        email: email.trim(),
        jmeno: jmeno.trim(),
        strana: stranaFinal,
        roles,
        // Kdyz je zaskrtnut "poslat reset link", neposilame heslo a backend
        // vygeneruje token a posle e-mail. Jinak pouzijeme zadane heslo.
        hesloPlain: sendResetLink ? null : heslo,
      };
      onCreate(input);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editItem ? 'Upravit uživatele' : 'Nový uživatel'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="user-email">E-mail</Label>
            <Input
              id="user-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user-jmeno">Jméno</Label>
            <Input
              id="user-jmeno"
              required
              value={jmeno}
              onChange={(e) => setJmeno(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Role</Label>
            <div
              className="space-y-1 rounded-md border p-3"
              title={isSelfEdit ? 'Nelze upravit vlastni ucet (role)' : undefined}
            >
              {ALL_ROLES.map((r) => (
                <label
                  key={r}
                  className={
                    'flex items-center gap-2 text-sm ' +
                    (isSelfEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')
                  }
                >
                  <input
                    type="checkbox"
                    checked={roles.includes(r)}
                    disabled={isSelfEdit}
                    onChange={() => toggleRole(r)}
                    className="h-4 w-4 rounded border-input"
                  />
                  {ROLE_LABELS[r]}
                </label>
              ))}
            </div>
            {isSelfEdit && (
              <p className="text-xs text-muted-foreground">
                Nelze upravit vlastni ucet (role/aktivni). Email a jmeno menit lze.
              </p>
            )}
            {!isSelfEdit && roles.length === 0 && (
              <p className="text-xs text-destructive">Vyberte alespoň jednu roli.</p>
            )}
            {!isSelfEdit && rolesMix && (
              <p className="text-xs text-destructive">
                Nelze kombinovat role Green Gas DPB a externí firmy na jednom účtu.
              </p>
            )}
          </div>

          {!editItem && (
            <>
              <div className="flex items-center gap-2">
                <input
                  id="user-sendresetlink"
                  type="checkbox"
                  checked={sendResetLink}
                  onChange={(e) => setSendResetLink(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="user-sendresetlink">
                  Vygenerovat reset link a poslat e-mailem
                </Label>
              </div>
              {!sendResetLink && (
                <div className="space-y-1.5">
                  <Label htmlFor="user-heslo">Heslo</Label>
                  <Input
                    id="user-heslo"
                    type="password"
                    required
                    value={heslo}
                    onChange={(e) => setHeslo(e.target.value)}
                    autoComplete="new-password"
                    aria-invalid={hesloDuvody.length > 0}
                  />
                  {hesloDuvody.length > 0 ? (
                    <ul className="space-y-0.5 text-xs text-destructive">
                      {hesloDuvody.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground">{HESLO_HINT}</p>
                  )}
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            <Button type="submit" disabled={submitting || hesloVadne || (!isSelfEdit && rolesVadne)}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uložit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface SetPasswordDialogProps {
  /** Uzivatel, jehoz heslo nastavujeme; null = dialog zavreny. */
  item: UserListItem | null;
  onClose: () => void;
  onSubmit: (item: UserListItem, sendResetLink: boolean, heslo: string) => void;
  submitting: boolean;
}

// Dialog nad existujicim uzivatelem — stejny princip jako create dialog:
// bud poslat reset-link e-mailem, nebo zadat heslo a ulozit primo (PUT .../heslo).
// Vlastni ucet zde NEblokujeme — endpoint dovoluje adminovi menit i vlastni heslo.
function SetPasswordDialog({ item, onClose, onSubmit, submitting }: SetPasswordDialogProps) {
  const open = item != null;
  const [sendResetLink, setSendResetLink] = useState(true);
  const [heslo, setHeslo] = useState('');
  const [hesloConfirm, setHesloConfirm] = useState('');

  useEffect(() => {
    if (open) {
      setSendResetLink(true);
      setHeslo('');
      setHesloConfirm('');
    }
  }, [open]);

  // Kontrola, ze admin zadal heslo spravne (shoda obou poli). Plati jen pri
  // primem nastaveni hesla — u reset-linku se heslo nezadava.
  const hesloMismatch = !sendResetLink && hesloConfirm.length > 0 && heslo !== hesloConfirm;
  // Policy validace zrcadli backend (lib/passwordPolicy.ts). E-mail uzivatele
  // je k dispozici (item.email) — pouzijeme ho ke kontrole.
  const hesloDuvody =
    !sendResetLink && heslo.length > 0 ? kontrolaHesla(heslo, item?.email) : [];
  const canSubmit =
    !submitting &&
    (sendResetLink ||
      (heslo.length > 0 && heslo === hesloConfirm && hesloDuvody.length === 0));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!item) return;
    if (!sendResetLink && heslo !== hesloConfirm) return; // pojistka
    onSubmit(item, sendResetLink, heslo);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nastavit heslo</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {item && (
            <p className="text-sm text-muted-foreground">
              Uživatel <span className="font-medium text-foreground">{item.jmeno}</span>{' '}
              <span className="font-mono">({item.email})</span>
            </p>
          )}
          <div className="flex items-center gap-2">
            <input
              id="setpwd-sendresetlink"
              type="checkbox"
              checked={sendResetLink}
              onChange={(e) => setSendResetLink(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="setpwd-sendresetlink">
              Vygenerovat reset link a poslat e-mailem
            </Label>
          </div>
          {!sendResetLink && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="setpwd-heslo">Heslo</Label>
                <Input
                  id="setpwd-heslo"
                  type="password"
                  required
                  value={heslo}
                  onChange={(e) => setHeslo(e.target.value)}
                  autoComplete="new-password"
                  aria-invalid={hesloDuvody.length > 0}
                />
                {hesloDuvody.length > 0 ? (
                  <ul className="space-y-0.5 text-xs text-destructive">
                    {hesloDuvody.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">{HESLO_HINT}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="setpwd-heslo-confirm">Heslo znovu</Label>
                <Input
                  id="setpwd-heslo-confirm"
                  type="password"
                  required
                  value={hesloConfirm}
                  onChange={(e) => setHesloConfirm(e.target.value)}
                  autoComplete="new-password"
                  aria-invalid={hesloMismatch}
                />
                {hesloMismatch && (
                  <p className="text-xs text-destructive">Hesla se neshodují.</p>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uložit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
